import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["counter", "errorCondition", "label", "max", "maxLength", "renderInput", "value", "data-test-id"];
import { isValidElement, useEffect, useState } from 'react';
import { defaultCounters, defaultLabeler } from './CharacterCounter.utils';
import devLogger from 'react-utils/devLogger';
import { TextInput } from '../TextInput/TextInput';
import { TextArea } from '../TextArea/TextArea';
import { ControllableNext as Controllable } from '../Controllable/ControllableNext';
import styled from 'foundations-theming/utils';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const supportsCharacterCount = renderedInput => {
  if (! /*#__PURE__*/isValidElement(renderedInput)) return false;
  return 'characterCount' in renderedInput.props;
};
const defaultRenderers = {
  text: props => /*#__PURE__*/_jsx(TextInput, Object.assign({}, props)),
  textarea: props => /*#__PURE__*/_jsx(TextArea, Object.assign({}, props))
};
const getCounterFunction = counter => {
  if (typeof counter === 'string') {
    return defaultCounters[counter];
  }
  return counter;
};
const StyledWrapper = styled.div.withConfig({
  displayName: "CharacterCounter__StyledWrapper",
  componentId: "sc-3xicg1-0"
})(({
  $cssStyles
}) => {
  return Object.assign({
    display: 'flex',
    flexDirection: 'column'
  }, $cssStyles);
});
const StyledLabel = styled.div.withConfig({
  displayName: "CharacterCounter__StyledLabel",
  componentId: "sc-3xicg1-1"
})(({
  $error,
  theme
}) => {
  return {
    alignSelf: 'flex-end',
    color: $error ? theme.color['text-alert-default'] : theme.color['text-core-subtle'],
    fontSize: theme.typography['body-100'].fontSize,
    marginInlineStart: theme.space['200'],
    padding: `${theme.space['200']} ${theme.space['0']} ${theme.space['100']}`
  };
});
export const InternalCharacterCounter = _ref => {
  let {
      counter = 'up',
      errorCondition = count => count < 0,
      label: _label = defaultLabeler,
      max = 250,
      maxLength,
      renderInput = 'textarea',
      value = '',
      'data-test-id': dataTestId
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (process.env.NODE_ENV !== 'production') {
    if (typeof counter === 'function' && _label === defaultLabeler) {
      devLogger.warn({
        key: 'UICharacterCounter missing label generator',
        message: 'UICharacterCounter: if you provide a `counter` function you ' + 'must also provide a corresponding `label` function. The default ' + 'label generator only works with the standard "up" or "down" counters.'
      });
    }
  }
  const [count, setCount] = useState((value === null || value === void 0 ? void 0 : value.length) || 0);
  const [label, setLabel] = useState(null);
  const [prevVal, setPrevVal] = useState(value);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  useEffect(() => {
    const counterFunction = getCounterFunction(counter);
    const nextCount = counterFunction ? counterFunction(prevVal, value, {
      max,
      maxLength
    }) : 0;
    const nextLabel = typeof _label === 'function' ? _label({
      count: nextCount
    }, counter) : _label;
    setCount(nextCount);
    setLabel(nextLabel);
    setPrevVal(value);
  }, [counter, _label, max, maxLength, value, prevVal]);
  const renderedInput = typeof renderInput === 'string' ? defaultRenderers[renderInput](Object.assign({}, rest, {
    'data-test-id': dataTestId,
    error: errorCondition(count, value),
    value,
    maxLength
  })) : renderInput(Object.assign({}, rest, dataTestId ? {
    'data-test-id': dataTestId
  } : {}, {
    count,
    maxLength,
    value
  }));
  const renderedLabel = supportsCharacterCount(renderedInput) ? null : /*#__PURE__*/_jsx(StyledLabel, {
    $error: errorCondition(count, value),
    "aria-live": "polite",
    children: label
  });
  return /*#__PURE__*/_jsxs(StyledWrapper, Object.assign({
    $cssStyles: cssStyles
  }, domProps, {
    children: [renderedInput, renderedLabel]
  }));
};
export const CharacterCounter = Controllable(InternalCharacterCounter);